import type { ParsedRbgColor } from "./wcag.convert-color-to-rgb";
import { convertColorStringToRgb } from "./wcag.convert-color-to-rgb";

export type ContrastRatioOptions = {
  threshold?: "AA-laxed" | "AA" | "AAA";
  largeText?: boolean;
  laxRatio?: number;
};

/**
 * determine if the contrast ratio between the two given colors is high enough
 * to meet the criterias defined by WCAG
 *
 * based on the contrast ratio definition from W3C:
 * @see https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 *
 * and the thresholds defined by WCAG:
 * @see https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
 * @see https://www.w3.org/TR/UNDERSTANDING-WCAG20/visual-audio-contrast7.html
 */
export function verifyWCAGContrastRatio(
  color1: string | undefined,
  color2: string | undefined,
  options: ContrastRatioOptions = {},
): boolean {
  const rgbColor1 = convertColorStringToRgb(color1);
  const rgbColor2 = convertColorStringToRgb(color2);

  if (!rgbColor1 || !rgbColor2) {
    return false;
  }

  // determine minimum allowed contrast ratio based on options
  const threshold =
    (() => {
      switch (options.threshold) {
        case "AAA":
          return options.largeText ? 4.5 : 7;

        case "AA-laxed":
          // Gyldendal specific "laxed" AA ratio, which isn't strictly WCAG AA
          // compliant, but due to internal restrictions in color choices, this
          // should always result in compliant layout
          //
          // and in cases where editors have not yet chosen an approved color
          // for their layouts, we prefer catering to users without specific
          // accessibility needs
          return 3;

        case "AA":
        default:
          return options.largeText ? 3 : 4.5;
      }
    })() - (options.laxRatio ?? 0);

  // calculate luminosity for both given colors, and check to see if we meet the
  // required threshold
  const L1 = calculateColorLuminosity(rgbColor1) + 0.05;
  const L2 = calculateColorLuminosity(rgbColor2) + 0.05;

  return Math.max(L1, L2) / Math.min(L1, L2) >= threshold;
}

/**
 * calculates relative luminosity of an rgb color
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function calculateColorLuminosity(color: ParsedRbgColor): number {
  const [r, g, b] = [color.r, color.g, color.b].map((c) => {
    c = c / 255;

    if (c <= 0.03928) {
      return c / 12.92;
    } else {
      return ((c + 0.055) / 1.055) ** 2.4;
    }
  }) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
